import type { ReactNode } from 'react';

/**
 * RingProgress — multi-segment donut.
 *
 * Pure inline SVG, no library. Each segment is a `<circle>` with
 * `strokeDasharray` calibrated to its share of the circumference;
 * segments are concatenated using `strokeDashoffset` so they
 * don't overlap.
 *
 * `label` renders centered inside the donut hole.
 */

interface RingSegment {
  value: number; // 0..100
  color: string; // any valid CSS color; pass Tailwind theme vars
}

interface RingProgressProps {
  size?: number;
  thickness?: number;
  sections: readonly RingSegment[];
  label?: ReactNode;
  // Pure cosmetic — round the cap of each arc.
  roundCaps?: boolean;
}

export function RingProgress({
  size = 80,
  thickness = 8,
  sections,
  label,
  roundCaps = true,
}: RingProgressProps) {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  let cumulativePct = 0;
  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden
      >
        {/* Track ring under the segments so partial donuts still
            read as "100% area assigned". */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="var(--color-neutral-800)"
          strokeWidth={thickness}
        />
        {sections.map((seg, idx) => {
          const dashLength = (seg.value / 100) * circumference;
          const dashOffset = -((cumulativePct / 100) * circumference);
          cumulativePct += seg.value;
          return (
            <circle
              key={idx}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth={thickness}
              strokeDasharray={`${dashLength} ${circumference - dashLength}`}
              strokeDashoffset={dashOffset}
              strokeLinecap={roundCaps ? 'round' : 'butt'}
              // Start at 12 o'clock and progress clockwise.
              transform={`rotate(-90 ${center} ${center})`}
            />
          );
        })}
      </svg>
      {label && (
        <div className="absolute inset-0 flex items-center justify-center">
          {label}
        </div>
      )}
    </div>
  );
}
